import React, { useEffect, useState, useMemo, forwardRef } from "react";
import { Chart, ChartCanvas } from "@react-financial-charts/core";
import { XAxis, YAxis } from "@react-financial-charts/axes";
import { format } from "d3-format";
import { discontinuousTimeScaleProviderBuilder } from "@react-financial-charts/scales";
import { LineSeries } from "@react-financial-charts/series";
import { lastVisibleItemBasedZoomAnchor } from "react-financial-charts";
import { calculateADX } from "../../../utils/indicatorCalculations";

const ADXIndicator = forwardRef(({
  height,
  ratio,
  width,
  initialData,
  xAccessor,
  displayXAccessor,
  xScale,
  xExtents,
  showXAxis = true,
  windowSize = 14,
  margin,
  padding,
}, ref) => {
  const [processedData, setProcessedData] = useState([]);

  const xScaleProvider = useMemo(
    () => discontinuousTimeScaleProviderBuilder().inputDateAccessor((d) => d.date),
    []
  );

  useEffect(() => {
    if (!initialData || initialData.length === 0) {
      setProcessedData([]);
      return;
    }
    const formattedData = initialData.map((item) => ({
      ...item,
      date: new Date(item.timestamp || item.date),
    }));
    const result = calculateADX(formattedData, windowSize);
    setProcessedData(result);
  }, [initialData, windowSize]);

  const { data } = xScaleProvider(processedData);

  const yExtents = useMemo(() => {
    return (d) => {
      const values = [d?.ADX, d?.DMN, d?.DMP].filter(
        (v) => v !== null && v !== undefined && Number.isFinite(v)
      );
      if (values.length === 0) return [0, 100];
      return [Math.min(...values), Math.max(...values)];
    };
  }, []);

  const calculateOptimalTicks = (chartHeight) => {
    const baseTickNumber = Math.floor(chartHeight / 50);
    return Math.max(baseTickNumber, 3);
  };
  const optimalTicks = calculateOptimalTicks(height);

  if (!data || data.length === 0) {
    return (
      <div style={{ height, width, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: '#666' }}>Loading ADX...</span>
      </div>
    );
  }

  return (
    <ChartCanvas
      ref={ref}
      height={height}
      ratio={ratio}
      width={width}
      data={data}
      displayXAccessor={displayXAccessor}
      seriesName="ADX"
      xScale={xScale}
      xAccessor={xAccessor}
      xExtents={xExtents}
      margin={margin}
      padding={padding}
      disableZoom={false}
      disablePan={false}
      disableInteraction={false}
      clamp={false}
      zoomAnchor={lastVisibleItemBasedZoomAnchor}
    >
      {/* Client: "When you have two indicators, the top of the top indicator and
          the bottom of the bottom indicator are cut off. You have to scroll out
          on the chart to see the RSI under 30 and the ADL at its current level.
          Can we add some padding without messing up these indicators staying
          connected to price when a user scrolls in or out?"

          `padding` on <Chart> is exactly that guarantee. It shrinks the y RANGE
          by 8px at each end — the plot is drawn 16px shorter inside the same
          panel — and leaves the y DOMAIN, the x scale and xExtents untouched.
          Horizontal sync with the price chart is driven entirely by the shared
          xScale/xExtents, so panning and zooming stay locked to price exactly as
          before; only the vertical breathing room changes. */}
      <Chart id={1} yExtents={yExtents} padding={{ top: 8, bottom: 8 }}>
        {showXAxis ? (
          <XAxis
            axisAt="bottom"
            orient="bottom"
            strokeStyle="#8d8d8d"
            tickStrokeStyle="#8d8d8d"
            tickLabelFill="#4a4a4a"
          />
        ) : (
          <XAxis
            strokeStyle="#e5e7eb"
            tickStrokeStyle="#e5e7eb"
            showTicks={false}
            showTickLabel={false}
            xZoomHeight={1}
            tickPadding={0}
          />
        )}
        <YAxis
          strokeStyle="#e5e7eb"
          tickStrokeStyle="#e5e7eb"
          tickFormat={format(".1f")}
          ticks={optimalTicks}
        />
        <XAxis
          strokeStyle="#e5e7eb"
          tickStrokeStyle="#e5e7eb"
          showTicks={false}
          xZoomHeight={1}
          tickPadding={0}
          axisAt="top"
          orient="top"
        />
        <YAxis
          strokeStyle="#e5e7eb"
          tickStrokeStyle="#e5e7eb"
          showTicks={false}
          yZoomWidth={1}
          tickPadding={0}
          axisAt="left"
          orient="left"
        />

        {/* ADX Line - Green */}
        <LineSeries
          yAccessor={(d) => d?.ADX}
          strokeStyle="#00FF00"
          strokeWidth={1.5}
        />

        {/* -DI Line (DMN) - Blue */}
        <LineSeries
          yAccessor={(d) => d?.DMN}
          strokeStyle="#0000FF"
          strokeWidth={1.5}
        />

        {/* +DI Line (DMP) - Red */}
        <LineSeries
          yAccessor={(d) => d?.DMP}
          strokeStyle="#FF0000"
          strokeWidth={1.5}
        />
      </Chart>
    </ChartCanvas>
  );
});

export default ADXIndicator;
